/* eslint-disable prettier/prettier */
/* eslint-disable no-irregular-whitespace */
import React, { FC, useEffect, useState } from 'react';
import {
  Col,
  Form,
  Row,
  Select as BaseSelect,
  FormInstance,
  Alert,
  Popover,
  Switch,
  Button,
} from 'antd';
import {
  Input,
  Select,
  useMaxBreakpoint,
  InputNumber,
} from '@cloudcar-app/cloudcar-components';
import getScreenSpacings from 'utils/getScreenSpacings';
import MessageError from 'utils/message.errors';
import rutFormValidator from 'utils/rutFormValidator';
import { CarCondition, InsuranceUseType, PaymentType } from 'apollo/types';
import { InfoCircleOutlined, QuestionCircleTwoTone } from '@ant-design/icons';
import { formatRut, validateRut } from '@abstract-cl/rut-ts';
import fakePhoneValidator from 'utils/fakePhoneValidator';
import { useLazyQuery } from '@apollo/client';
import operations from 'apollo/operations';
import { vehicleTypes } from 'utils/kaufmann/vehicleTypesMappers';
import getSalesDetails, { Ficha } from 'utils/kaufmannSalesIntegrationService';
import { trimFormValues } from 'utils/trimFormValues';
import styles from './InsuranceInformationForm.module.scss';

interface InsuranceFormInformationProps {
  form: FormInstance;
  insuranceEnabled: boolean;
  setInsuranceEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  showInternalQuotationId: boolean;
  showCustomerRut: boolean;
}

const InsuranceFormInformation: FC<InsuranceFormInformationProps> = (
  props: InsuranceFormInformationProps,
) => {
  const {
    form,
    insuranceEnabled,
    setInsuranceEnabled,
    showInternalQuotationId,
    showCustomerRut,
  } = props;

  const [brands, setBrands] = useState<string[] | null>(null);
  const [checkedBusinessName, setCheckedBusinessName] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [models, setModels] = useState<string[] | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const { Option } = BaseSelect;
  const [privateVehicleUse, setPrivateVehicleUse] = useState(true);
  const [insuranceRutIsEqualToOwnerRut, setInsuranceRutIsEqualToOwnerRut] =
    useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const [isCarSemiNew, setIsCarSemiNew] = useState(false);

  const maxScreen = useMaxBreakpoint();
  const screenSpacings = getScreenSpacings(maxScreen);

  const handleChangeRut = (rut: string): string => {
    if (validateRut(rut)) {
      const formatted: string = formatRut(rut) || '';
      form.setFieldsValue({ rut: formatted });
      return rut;
    }
    form.setFieldsValue({
      rut: rut.split('.').join('').split('-').join(''),
    });
    return rut;
  };

  const handleInsuredRut = (rut: string): string => {
    if (validateRut(rut)) {
      const formatted: string = formatRut(rut) || '';
      form.setFieldsValue({ insuranceRut: formatted });
      return rut;
    }
    form.setFieldsValue({
      insuranceRut: rut.split('.').join('').split('-').join(''),
    });
    return rut;
  };

  const insuranceInterestOptions = [
    { label: 'Dueño o propietario', value: 'DUEÑO O PROPIETARIO' },
    { label: 'Hijos', value: 'HIJOS' },
    { label: 'Padres', value: 'PADRES' },
    { label: 'Cónyuge o pareja', value: 'CONYUGE O PAREJA' },
  ];

  const paymentTypes = [
    { label: 'Cuotas (Tarjeta de crédito)', value: PaymentType.Installments },
    {
      label: 'Cuotas (PAC)',
      value: PaymentType.Pac,
    },
    { label: 'Contado (Emisión disponible solo HDI)', value: PaymentType.Cash },
  ];

  useEffect(() => {
    setBrands(['Maxus', 'M.Benz', 'Smart']);
  }, []);

  const [getModels, { data: modelsData, loading }] = useLazyQuery<{
    getModels: string[];
  }>(operations.GET_MODELS, {
    fetchPolicy: 'no-cache',
  });
  useEffect(() => {
    if (selectedBrand) {
      getModels({ variables: { brand: selectedBrand } });
    }
  }, [selectedBrand]);

  useEffect(() => {
    if (modelsData?.getModels) {
      setModels(modelsData.getModels);
    }
  }, [modelsData]);

  useEffect(() => {
    if (checkedBusinessName) {
      setInsuranceRutIsEqualToOwnerRut(true);
    }
  }, [checkedBusinessName]);

  useEffect(() => {
    if (insuranceRutIsEqualToOwnerRut) {
      form.setFieldsValue({ insuranceRutIsEqualToOwnerRut: true });
    } else {
      form.setFieldsValue({ insuranceRutIsEqualToOwnerRut: false });
    }
  }, [insuranceRutIsEqualToOwnerRut]);

  useEffect(() => {
    if (!privateVehicleUse) {
      form.setFieldsValue({
        privateVehicleUse: false,
        insuranceInterest: !insuranceRutIsEqualToOwnerRut
          ? 'Dueño o propietario'
          : undefined,
      });
    } else {
      form.setFieldsValue({
        privateVehicleUse: true,
        insuranceInterest: undefined,
      });
    }
  }, [privateVehicleUse, insuranceRutIsEqualToOwnerRut]);

  const carModelHasInsuranceEnabled = () => {
    if (selectedModel && selectedModel.includes('ALTO COSTO')) {
      setInsuranceEnabled(false);
    } else {
      setInsuranceEnabled(true);
    }
  };

  const handleSelectedModel = (model: string) => {
    setSelectedModel(model);
  };

  const handleCarInfomation = (ficha: Ficha) => {
    const { unidadInfo } = ficha;
    const brandMapping: Record<string, string> = {
      MAXUS: 'Maxus',
      'MERCEDES-BENZ': 'M.Benz',
      smart: 'Smart',
    };

    const brand = brandMapping[unidadInfo.descSector] || null;

    setSelectedBrand(brand);
    form.setFields([
      {
        name: 'brand',
        value: brand,
        touched: true,
        validating: false,
      },
    ]);
  };

  const handleNroFicha = async () => {
    setIsLoading(true);
    const nroFicha = trimFormValues(
      form.getFieldValue('quotationId') as string,
    ) as string;
    if (!nroFicha) {
      console.log('No nroFicha');
      setIsLoading(false);
      return;
    }
    try {
      const salesDetails = await getSalesDetails(nroFicha);
      handleCarInfomation(salesDetails);
      form.setFields([
        {
          name: 'rut',
          value: salesDetails.clienteInfo.IdentificadorFiscal,
          touched: true,
          validating: false,
        },
        {
          name: 'insuranceFirstName',
          value: salesDetails.clienteInfo.NombresRazonSocialCuenta,
          touched: true,
          validating: false,
        },
        {
          name: 'insuranceLastName',
          value: salesDetails.clienteInfo.ApellidosCuenta,
          touched: true,
          validating: false,
        },
        {
          name: 'carPlate',
          value: salesDetails.unidadInfo.patente,
          touched: true,
          validating: false,
        },
        {
          name: 'email',
          value: salesDetails.clienteInfo.Mail,
          touched: true,
          validating: false,
        },
      ]);
    } catch (error) {
      console.log('Error fetching sales details:', error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    carModelHasInsuranceEnabled();
  }, [selectedModel]);

  return (
    <Row
      gutter={[screenSpacings.L, screenSpacings.L]}
      justify="start"
      align="middle"
      style={{ width: '100%' }}
    >
      {!insuranceEnabled && (
        <Col xs={13}>
          <Alert
            showIcon
            style={{ borderRadius: '4px' }}
            icon={<InfoCircleOutlined className={styles.AlertIcon} />}
            message={
              <>
                <p className={styles.AlertTitle}>
                  Vehículo de Alto Costo, cotizar seguro con Gestor Comercial.
                </p>
              </>
            }
            type="info"
          />
        </Col>
      )}

      <Col xs={24}>
        <h3> Datos del seguro</h3>
      </Col>
      {showInternalQuotationId && (
        <Col xs={24} sm={8}>
          <Form.Item name="quotationId" className={styles.FormInput}>
            <Input
              label="Número de ficha de venta del vehículo"
              placeholder="N° ficha de venta del vehículo"
              compact
              className={styles.Input}
              suffix={
                <Button
                  type="text"
                  icon={<QuestionCircleTwoTone />}
                  onClick={handleNroFicha}
                  loading={isLoading}
                >
                  Buscar
                </Button>
              }
            />
          </Form.Item>
        </Col>
      )}
      {showCustomerRut && (
        <Col xs={24} sm={8}>
          <Form.Item
            name="rut"
            className={styles.FormInput}
            rules={[
              {
                required: true,
                message: MessageError.emptyFieldsMessages.rut,
              },
              {
                whitespace: true,
                message: MessageError.emptyFieldsMessages.rut,
              },
              {
                validator: rutFormValidator,
              },
            ]}
          >
            <Input
              label="Rut cliente"
              placeholder="Rut cliente"
              onChange={(e) => handleChangeRut(e.target.value)}
              required
              compact
              className={styles.Input}
            />
          </Form.Item>
        </Col>
      )}
      <Col xs={24} sm={8}>
        <Form.Item
          name="insuranceUseType"
          className={styles.FormInput}
          rules={[
            {
              required: true,
              message: MessageError.emptyFieldsMessages.InsuranceUseType,
            },
            {
              whitespace: true,
              message: MessageError.emptyFieldsMessages.InsuranceUseType,
            },
          ]}
        >
          <Select
            showSearch
            placeholder="Tipo de seguro (uso)"
            onChange={(value: string) => {
              if (value === InsuranceUseType.Private) {
                setPrivateVehicleUse(true);
              } else {
                setPrivateVehicleUse(false);
              }
            }}
          >
            {[
              {
                value: InsuranceUseType.Private,
                label: 'Particular',
              },
              {
                value: InsuranceUseType.Commercial,
                label: 'Comercial',
              },
            ].map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={8}>
        <Form.Item
          name="rutType"
          className={styles.FormInput}
          rules={[
            {
              required: true,
              message: MessageError.emptyFieldsMessages.rutType,
            },
            {
              whitespace: true,
              message: MessageError.emptyFieldsMessages.rutType,
            },
          ]}
        >
          <Select
            showSearch
            placeholder="Tipo de cliente (rut)"
            onChange={(value: string) => {
              if (value === 'empresa') {
                setCheckedBusinessName(true);
              } else {
                setCheckedBusinessName(false);
              }
            }}
          >
            {[
              {
                value: 'natural',
                label: 'Natural',
              },
              {
                value: 'empresa',
                label: 'Empresa',
              },
            ].map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={8}>
        <Form.Item
          name="paymentType"
          className={styles.FormInput}
          rules={[
            {
              required: true,
              message: MessageError.emptyFieldsMessages.paymentType,
            },
            {
              whitespace: true,
              message: MessageError.emptyFieldsMessages.paymentType,
            },
          ]}
        >
          <Select
            showSearch
            placeholder="Tipo de pago"
          >
            {paymentTypes.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24}>
        <h3>Datos vehículo</h3>
      </Col>
      <>
        <Col xs={24} sm={8}>
          <Form.Item
            name="brand"
            className={styles.FormInput}
            rules={[
              {
                required: true,
                message: MessageError.emptyFieldsMessages.brand,
              },
            ]}
          >
            <Select
              showSearch
              placeholder="Marca"
              onChange={(value: string) => {
                setSelectedBrand(value);
                setSelectedModel(null);
              }}
            >
              {brands?.map((vehicleBrand) => (
                <Option key={vehicleBrand} value={vehicleBrand}>
                  {vehicleBrand}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name="model"
            className={styles.FormInput}
            rules={[
              {
                required: true,
                message: MessageError.emptyFieldsMessages.model,
              },
            ]}
          >
            <Select
              disabled={loading}
              showSearch
              placeholder="Modelo"
              onChange={(value: string) => handleSelectedModel(value)}
            >
              {models?.map((model) => (
                <Option key={model} value={model}>
                  {model}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={8}>
          <Form.Item
            name="year"
            className={styles.FormInput}
            rules={[
              {
                required: true,
                message: MessageError.emptyFieldsMessages.year,
              },
              {
                type: 'number',
                message: MessageError.invalidFormatFieldsMessages.year,
              },
            ]}
          >
            <InputNumber
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              label="Año"
              placeholder="Año"
              required
              compact
              className={styles.Input}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={8}>
          <Form.Item
            name="vehicleType"
            className={styles.FormInput}
            rules={[
              {
                required: true,
                message: MessageError.emptyFieldsMessages.vehicleType,
              },
            ]}
          >
            <Select
              compact
              label="Tipo de vehículo"
              placeholder="Tipo de vehículo"
              options={vehicleTypes.map((option) => ({
                label: option.label,
                value: option.value,
              }))}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={8}>
          <Form.Item
            name="carCondition"
            className={styles.FormInput}
            rules={[
              {
                required: true,
                message: MessageError.emptyFieldsMessages.carCondition,
              },
            ]}
          >
            <Select
              showSearch
              placeholder="Condición del vehículo"
              onChange={(value) => {
                if (value === CarCondition.Seminew || value === CarCondition.Used) {
                  setIsCarSemiNew(true);
                } else {
                  setIsCarSemiNew(false);
                }
              }}
            >
              {[
                {
                  value: CarCondition.New,
                  label: 'Nuevo',
                },
                {
                  value: CarCondition.Seminew,
                  label: 'Semi nuevo',
                },
                // {
                //   value: CarCondition.Used,
                //   label: 'Usado',
                // },
              ]
                .filter(
                  (option) =>
                    !selectedModel ||
                    selectedModel.includes('ZURICH') ||
                    option.value !== CarCondition.Used,
                )
                .map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        {isCarSemiNew && (
          <Col xs={24} sm={8}>
            <Form.Item
              name="carPlate"
              className={styles.FormInput}
              rules={[
                {
                  required: true,
                  message: MessageError.emptyFieldsMessages.carChassis,
                },
                {
                  whitespace: true,
                  message: MessageError.emptyFieldsMessages.carChassis,
                },
                {
                  pattern: /^[A-Za-z0-9]{6}$/,
                  message:
                    'La patente debe contener solo letras y números. Debe tener 6 caracteres.',
                },
              ]}
            >
              <Input
                onChange={(e) => {
                  const filteredValue = e.target.value
                    .replace(/[^A-Za-z0-9]/g, '')
                    .slice(0, 6);
                  form.setFieldsValue({ carPlate: filteredValue });
                }}
                label="Patente"
                placeholder="Patente (sin puntos ni guión)"
                required
                compact
                className={styles.Input}
              />
            </Form.Item>
          </Col>
        )}
      </>

      <Col xs={24}>
        <h3>Datos asegurado</h3>
      </Col>
      {!checkedBusinessName && (
        <Col xs={24}>
          <Row
            gutter={12}
            className={styles.FinancingSwitch}
            style={{ display: 'flex', flexDirection: 'row' }}
          >
            <Col>
              <Switch
                checked={!insuranceRutIsEqualToOwnerRut}
                onChange={() =>
                  setInsuranceRutIsEqualToOwnerRut(
                    !insuranceRutIsEqualToOwnerRut,
                  )
                }
              />
            </Col>
            <Col style={{ display: 'flex' }}>
              <p>Rut asegurado distinto al propietario</p>
              <Popover
                overlayClassName={styles.Popover}
                content="Esta opción solo está disponible para BCI Y HDI, y el interés asegurable solo aplica para BCI."
                className={styles.Popover}
              >
                <QuestionCircleTwoTone className={styles.InfoCircleTwoTone} />
              </Popover>
            </Col>
          </Row>
        </Col>
      )}

      {!insuranceRutIsEqualToOwnerRut && (
        <>
          <Col xs={24} sm={8}>
            <Form.Item
              name="insuranceInterest"
              className={styles.FormInput}
              rules={[
                {
                  required: true,
                  message: MessageError.emptyFieldsMessages.insuranceInterest,
                },
              ]}
            >
              {!privateVehicleUse ? (
                <Input disabled required compact />
              ) : (
                <Select placeholder="Interés asegurable" required compact>
                  {insuranceInterestOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="insuranceRut"
              className={styles.FormInput}
              rules={[
                {
                  required: true,
                  message: MessageError.emptyFieldsMessages.insuranceRut,
                },
                {
                  whitespace: true,
                  message: MessageError.emptyFieldsMessages.insuranceRut,
                },
                {
                  validator: rutFormValidator,
                },
              ]}
            >
              <Input
                onChange={(e) => handleInsuredRut(e.target.value)}
                label="RUT asegurado"
                placeholder="RUT asegurado"
                required
                compact
                className={styles.Input}
              />
            </Form.Item>
          </Col>
        </>
      )}
      {checkedBusinessName && insuranceRutIsEqualToOwnerRut && (
        <Col xs={24} sm={8}>
          <Form.Item
            name="businessName"
            className={styles.FormInput}
            rules={[
              {
                required: true,
                message: MessageError.emptyFieldsMessages.businessName,
              },
              {
                whitespace: true,
                message: MessageError.emptyFieldsMessages.businessName,
              },
            ]}
          >
            <Input
              label="Razon social"
              placeholder="Razon social"
              required
              compact
              className={styles.Input}
            />
          </Form.Item>
        </Col>
      )}
      {(!checkedBusinessName ||
        (checkedBusinessName && !insuranceRutIsEqualToOwnerRut)) && (
        <>
          <Col xs={24} sm={8}>
            <Form.Item
              name="insuranceFirstName"
              className={styles.FormInput}
              rules={[
                {
                  required: true,
                  message: MessageError.emptyFieldsMessages.name,
                },
                {
                  whitespace: true,
                  message: MessageError.emptyFieldsMessages.name,
                },
              ]}
            >
              <Input
                label="Nombre"
                placeholder="Nombre"
                required
                compact
                className={styles.Input}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="insuranceLastName"
              className={styles.FormInput}
              rules={[
                {
                  required: true,
                  message: MessageError.emptyFieldsMessages.lastName,
                },
                {
                  whitespace: true,
                  message: MessageError.emptyFieldsMessages.lastName,
                },
              ]}
            >
              <Input
                label="Apellido"
                placeholder="Apellido"
                required
                compact
                className={styles.Input}
              />
            </Form.Item>
          </Col>
        </>
      )}
      {/* <Col xs={24} sm={8}>
            <Form.Item
              name="commune"
              className={styles.FormInput}
              rules={[
                {
                  required: true,
                  message: MessageError.emptyFieldsMessages.commune,
                },
              ]}
            >
              <Select
                placeholder="Comuna"
                required
                compact
                showSearch
                filterOption={(input, option) =>
                  option
                    ? (
                      (option.children as unknown as string).toLowerCase() || ''
                    ).includes(input.toLowerCase())
                    : false
                }
              >
                {communes.zurichCommunes
                  .sort((a, b) => {
                    if (a.name < b.name) {
                      return -1;
                    }
                    if (a.name > b.name) {
                      return 1;
                    }
                    return 0;
                  })
                  .map((option) => (
                    <Option key={option.code} value={option.code}>
                      {option.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col> */}
      <Col xs={24} sm={8}>
        <Form.Item
          name="email"
          className={styles.FormInput}
          rules={[
            {
              required: true,
              message: MessageError.emptyFieldsMessages.email,
            },
            {
              whitespace: true,
              message: MessageError.emptyFieldsMessages.email,
            },
          ]}
        >
          <Input
            label="Correo electrónico"
            placeholder="Correo electrónico"
            required
            compact
            className={styles.Input}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={8}>
        <Form.Item
          name="phone"
          className={styles.FormInput}
          rules={[
            {
              required: true,
              message: MessageError.emptyFieldsMessages.phone,
            },
            {
              pattern: /^[9]\d{8}$/,
              message: MessageError.invalidFormatFieldsMessages.phone,
            },
            {
              whitespace: true,
              message: MessageError.emptyFieldsMessages.phone,
            },
            {
              validator: fakePhoneValidator,
            },
          ]}
        >
          <Input
            addonBefore="+56"
            label="Telefono"
            placeholder="Telefono"
            required
            compact
            className={styles.Input}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export default InsuranceFormInformation;
